import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Divider,
  CircularProgress,
  Chip,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Avatar,
  Tooltip,
} from '@mui/material';
import { 
  ArrowBack as BackIcon, 
  Edit as EditIcon, 
  Save as SaveIcon, 
  Print as PrintIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ViewKanban as BoardIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  CheckBox as TaskIcon,
  StickyNote2 as StickerIcon,
  Notes as NotesIcon,
  Archive as ArchiveIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

const CompanyDetail = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const printRef = useRef();
  
  // Состояние для истории
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Функция печати карточки компании
  const handlePrint = () => {
    if (!company) {
      alert('Данные компании еще не загружены');
      return;
    }
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Карточка компании - ${company.name}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            padding: 40px;
            color: #333;
            line-height: 1.6;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 3px solid #5e81ac;
          }
          .header h1 {
            color: #5e81ac;
            font-size: 28px;
            margin-bottom: 10px;
          }
          .header .status {
            display: inline-block;
            background: ${company.color || '#5e81ac'};
            color: white;
            padding: 5px 15px;
            border-radius: 20px;
            font-size: 14px;
          }
          .section {
            margin-bottom: 25px;
            page-break-inside: avoid;
          }
          .section-title {
            font-size: 18px;
            font-weight: 600;
            color: #5e81ac;
            margin-bottom: 15px;
            padding-bottom: 8px;
            border-bottom: 2px solid #e0e0e0;
          }
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
          }
          .info-row {
            display: flex;
            padding: 10px;
            background: #f5f5f5;
            border-radius: 5px;
          }
          .info-label {
            font-weight: 600;
            color: #666;
            min-width: 180px;
          }
          .info-value {
            color: #333;
            flex: 1;
          }
          .full-width {
            grid-column: 1 / -1;
          }
          .requisites-table {
            width: 100%;
            border-collapse: collapse;
          }
          .requisites-table td {
            padding: 10px;
            border: 1px solid #e0e0e0;
          }
          .requisites-table td:first-child {
            background: #f5f5f5;
            font-weight: 600;
            width: 35%;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #e0e0e0;
            text-align: center;
            color: #888;
            font-size: 12px;
          }
          .logo {
            font-size: 24px;
            font-weight: bold;
            color: #5e81ac;
            margin-bottom: 5px;
          }
          .managers {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
          }
          .manager-badge {
            background: #e3f2fd;
            color: #5e81ac;
            padding: 5px 12px;
            border-radius: 15px;
            font-size: 14px;
          }
          @media print {
            body { padding: 20px; }
            .section { margin-bottom: 20px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">Nord CRM</div>
          <h1>${company.name}</h1>
          <span class="status">${company.status || 'Активная'}</span>
        </div>

        <div class="section">
          <div class="section-title">📋 Основная информация</div>
          <div class="info-grid">
            <div class="info-row">
              <span class="info-label">Название компании:</span>
              <span class="info-value">${company.name || '—'}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Контактное лицо:</span>
              <span class="info-value">${company.contactPerson || '—'}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Телефон:</span>
              <span class="info-value">${company.phone || '—'}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Email:</span>
              <span class="info-value">${company.email || '—'}</span>
            </div>
            <div class="info-row full-width">
              <span class="info-label">Адрес:</span>
              <span class="info-value">${company.address || '—'}</span>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">🏦 Реквизиты предприятия</div>
          <table class="requisites-table">
            <tr>
              <td>ИНН</td>
              <td>${company.requisites?.inn || '—'}</td>
            </tr>
            <tr>
              <td>КПП</td>
              <td>${company.requisites?.kpp || '—'}</td>
            </tr>
            <tr>
              <td>ОГРН</td>
              <td>${company.requisites?.ogrn || '—'}</td>
            </tr>
            <tr>
              <td>Наименование банка</td>
              <td>${company.requisites?.bankName || '—'}</td>
            </tr>
            <tr>
              <td>БИК</td>
              <td>${company.requisites?.bik || '—'}</td>
            </tr>
            <tr>
              <td>Расчётный счёт</td>
              <td>${company.requisites?.checkingAccount || '—'}</td>
            </tr>
            <tr>
              <td>Корреспондентский счёт</td>
              <td>${company.requisites?.correspondentAccount || '—'}</td>
            </tr>
          </table>
        </div>

        ${company.additionalInfo ? `
        <div class="section">
          <div class="section-title">📝 Дополнительная информация</div>
          <div class="info-row full-width">
            <span class="info-value">${company.additionalInfo}</span>
          </div>
        </div>
        ` : ''}

        ${company.assignedTo && company.assignedTo.length > 0 ? `
        <div class="section">
          <div class="section-title">👥 Назначенные менеджеры</div>
          <div class="managers">
            ${company.assignedTo.map(m => `<span class="manager-badge">${m.firstName} ${m.lastName}</span>`).join('')}
          </div>
        </div>
        ` : ''}

        <div class="footer">
          <p>Документ сформирован: ${new Date().toLocaleDateString('ru-RU')} в ${new Date().toLocaleTimeString('ru-RU')}</p>
          <p>Nord CRM © ${new Date().getFullYear()}</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  useEffect(() => {
    fetchCompany();
  }, [id]);

  const fetchCompany = async () => {
    try {
      const response = await axios.get(`/api/companies/${id}`);
      setCompany(response.data);
      setFormData(response.data);
    } catch (error) {
      console.error('Ошибка загрузки компании:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await axios.patch(`/api/companies/${id}`, formData);
      setCompany(formData);
      setEditing(false);
    } catch (error) {
      console.error('Ошибка сохранения:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleRequisiteChange = (field, value) => {
    setFormData({
      ...formData,
      requisites: {
        ...formData.requisites,
        [field]: value,
      },
    });
  };

  // Загрузка истории компании
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await axios.get(`/api/companies/${id}/history`);
      setHistoryData(response.data);
    } catch (error) {
      console.error('Ошибка загрузки истории:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenHistory = () => {
    setHistoryOpen(true);
    fetchHistory();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const priorityColors = {
    'низкий': '#a3be8c',
    'средний': '#ebcb8b',
    'высокий': '#d08770',
    'критический': '#bf616a',
  };

  if (loading) {
    return (
      <Layout>
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="60vh"
          flexDirection="column"
          gap={2}
        >
          <CircularProgress size={60} thickness={2} />
          <Typography variant="body2" color="text.secondary">
            Загрузка компании...
          </Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
          <Button 
            startIcon={<BackIcon />} 
            onClick={() => navigate('/companies')} 
            sx={{ mr: 1 }}
            variant="outlined"
          >
            Назад
          </Button>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 800,
              background: isDarkMode 
                ? 'linear-gradient(135deg, #e6edf3 0%, #88c0d0 100%)'
                : 'linear-gradient(135deg, #2e3440 0%, #5e81ac 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {company.name}
          </Typography>
          <Chip
            label={company.status}
            size="small"
            sx={{
              ml: 1,
              backgroundColor: company.color,
              color: 'white',
              fontWeight: 600,
            }}
          />
        </Box>
        {company && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button 
              variant="contained"
              startIcon={<HistoryIcon />} 
              onClick={handleOpenHistory}
              sx={{
                background: 'linear-gradient(135deg, #b48ead 0%, #5e81ac 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5e81ac 0%, #b48ead 100%)',
                }
              }}
            >
              История
            </Button>
            <Button 
              variant="outlined"
              startIcon={<PrintIcon />} 
              onClick={handlePrint}
              disabled={!company}
            >
              Печать
            </Button>
            {editing ? (
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>
                Сохранить
              </Button>
            ) : (
              <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
                Редактировать
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Основная информация */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#ffffff',
              backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
              border: '1px solid',
              borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  📋 Основная информация
                </Typography>
                <Button 
                  size="small"
                  variant="outlined" 
                  startIcon={<PrintIcon />} 
                  onClick={handlePrint}
                  disabled={!company}
                >
                  Печать
                </Button>
              </Box>
              <Divider sx={{ mb: 2, opacity: 0.3 }} />

              <TextField
                fullWidth
                label="Название"
                value={formData.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                margin="normal"
                disabled={!editing}
              />

              <TextField
                fullWidth
                label="Адрес"
                value={formData.address || ''}
                onChange={(e) => handleChange('address', e.target.value)}
                margin="normal"
                disabled={!editing}
                multiline
                rows={2}
              />

              <TextField
                fullWidth
                label="Номер телефона"
                value={formData.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                margin="normal"
                disabled={!editing}
              />

              <TextField
                fullWidth
                label="Контактное лицо (ФИО)"
                value={formData.contactPerson || ''}
                onChange={(e) => handleChange('contactPerson', e.target.value)}
                margin="normal"
                disabled={!editing}
              />

              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                margin="normal"
                disabled={!editing}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Реквизиты */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#ffffff',
              backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
              border: '1px solid',
              borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>
                🏦 Реквизиты предприятия
              </Typography>
              <Divider sx={{ mb: 2, opacity: 0.3 }} />

              <TextField
                fullWidth
                label="ИНН"
                value={formData.requisites?.inn || ''}
                onChange={(e) => handleRequisiteChange('inn', e.target.value)}
                margin="normal"
                disabled={!editing}
              />

              <TextField
                fullWidth
                label="КПП"
                value={formData.requisites?.kpp || ''}
                onChange={(e) => handleRequisiteChange('kpp', e.target.value)}
                margin="normal"
                disabled={!editing}
              />

              <TextField
                fullWidth
                label="ОГРН"
                value={formData.requisites?.ogrn || ''}
                onChange={(e) => handleRequisiteChange('ogrn', e.target.value)}
                margin="normal"
                disabled={!editing}
              />

              <TextField
                fullWidth
                label="Название банка"
                value={formData.requisites?.bankName || ''}
                onChange={(e) => handleRequisiteChange('bankName', e.target.value)}
                margin="normal"
                disabled={!editing}
              />

              <TextField
                fullWidth
                label="БИК"
                value={formData.requisites?.bik || ''}
                onChange={(e) => handleRequisiteChange('bik', e.target.value)}
                margin="normal"
                disabled={!editing}
              />

              <TextField
                fullWidth
                label="Расчетный счет"
                value={formData.requisites?.checkingAccount || ''}
                onChange={(e) => handleRequisiteChange('checkingAccount', e.target.value)}
                margin="normal"
                disabled={!editing}
              />

              <TextField
                fullWidth
                label="Корреспондентский счет"
                value={formData.requisites?.correspondentAccount || ''}
                onChange={(e) => handleRequisiteChange('correspondentAccount', e.target.value)}
                margin="normal"
                disabled={!editing}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Дополнительная информация */}
        <Grid item xs={12}>
          <Card
            sx={{
              background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#ffffff',
              backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
              border: '1px solid',
              borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>
                📝 Дополнительная информация
              </Typography>
              <Divider sx={{ mb: 2, opacity: 0.3 }} />

              <TextField
                fullWidth
                label="Заметки"
                value={formData.additionalInfo || ''}
                onChange={(e) => handleChange('additionalInfo', e.target.value)}
                margin="normal"
                disabled={!editing}
                multiline
                rows={4}
                placeholder="Любая дополнительная информация о компании..."
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Назначенные менеджеры */}
        {company.assignedTo && company.assignedTo.length > 0 && (
          <Grid item xs={12}>
            <Card
              sx={{
                background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#ffffff',
                backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
                border: '1px solid',
                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>
                  👥 Назначенные менеджеры
                </Typography>
                <Divider sx={{ mb: 2, opacity: 0.3 }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {company.assignedTo.map((manager) => (
                    <Chip
                      key={manager._id}
                      label={`${manager.firstName} ${manager.lastName}`}
                      sx={{
                        background: 'rgba(136, 192, 208, 0.2)',
                        color: '#88c0d0',
                        border: '1px solid rgba(136, 192, 208, 0.3)',
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Модальное окно истории */}
      <Dialog 
        open={historyOpen} 
        onClose={() => setHistoryOpen(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            background: isDarkMode ? 'rgba(13, 17, 23, 0.98)' : '#ffffff',
            backdropFilter: 'blur(20px)',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: isDarkMode 
            ? 'linear-gradient(135deg, rgba(180, 142, 173, 0.15) 0%, rgba(94, 129, 172, 0.15) 100%)'
            : 'linear-gradient(135deg, #b48ead 0%, #5e81ac 100%)',
          color: isDarkMode ? '#e6edf3' : '#ffffff',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <HistoryIcon />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              История работы с компанией: {company?.name}
            </Typography>
          </Box>
          <IconButton onClick={() => setHistoryOpen(false)} sx={{ color: 'inherit' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <CircularProgress />
            </Box>
          ) : historyData ? (
            <Box>
              {/* Статистика */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    background: isDarkMode ? 'rgba(136, 192, 208, 0.1)' : 'rgba(136, 192, 208, 0.08)',
                    border: '1px solid rgba(136, 192, 208, 0.3)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#88c0d0' }}>
                      {historyData.stats?.totalCards || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Всего карточек
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    background: isDarkMode ? 'rgba(163, 190, 140, 0.1)' : 'rgba(163, 190, 140, 0.08)',
                    border: '1px solid rgba(163, 190, 140, 0.3)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#a3be8c' }}>
                      {formatCurrency(historyData.stats?.totalDealAmount || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Сумма сделок
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    background: isDarkMode ? 'rgba(180, 142, 173, 0.1)' : 'rgba(180, 142, 173, 0.08)',
                    border: '1px solid rgba(180, 142, 173, 0.3)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#b48ead' }}>
                      {historyData.stats?.uniqueManagers || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Менеджеров работало
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    background: isDarkMode ? 'rgba(235, 203, 139, 0.1)' : 'rgba(235, 203, 139, 0.08)',
                    border: '1px solid rgba(235, 203, 139, 0.3)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#ebcb8b' }}>
                      {historyData.stats?.completedTasks || 0}/{historyData.stats?.totalTasks || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Выполнено задач
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Период работы */}
              {historyData.stats?.firstInteraction && (
                <Box sx={{ 
                  mb: 3, 
                  p: 2, 
                  borderRadius: 2, 
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2
                }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Первое взаимодействие</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatDateTime(historyData.stats.firstInteraction)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Последнее обновление</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatDateTime(historyData.stats.lastInteraction)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* История карточек */}
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BoardIcon sx={{ color: '#88c0d0' }} />
                Хронология работы
              </Typography>

              {historyData.history && historyData.history.length > 0 ? (
                historyData.history.map((item, index) => (
                  <Accordion 
                    key={item._id}
                    sx={{
                      mb: 1,
                      background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#f8fafc',
                      border: '1px solid',
                      borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
                      borderLeft: `4px solid ${item.archived ? '#8b949e' : (priorityColors[item.priority] || '#88c0d0')}`,
                      '&:before': { display: 'none' },
                      borderRadius: '8px !important',
                      overflow: 'hidden',
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, flexWrap: 'wrap' }}>
                        {/* Дата */}
                        <Box sx={{ minWidth: 100 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(item.date)}
                          </Typography>
                        </Box>
                        
                        {/* Менеджер */}
                        <Chip
                          avatar={
                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>
                              {item.manager?.firstName?.[0]}{item.manager?.lastName?.[0]}
                            </Avatar>
                          }
                          label={`${item.manager?.firstName || ''} ${item.manager?.lastName || ''}`.trim() || 'Неизвестный'}
                          size="small"
                          sx={{ 
                            background: 'rgba(136, 192, 208, 0.15)',
                            border: '1px solid rgba(136, 192, 208, 0.3)',
                          }}
                        />
                        
                        {/* Доска и колонка */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={item.board?.name || 'Неизвестная доска'}
                            size="small"
                            sx={{ 
                              background: 'rgba(94, 129, 172, 0.15)',
                              border: '1px solid rgba(94, 129, 172, 0.3)',
                            }}
                          />
                          {item.column && (
                            <Chip
                              label={item.column.title}
                              size="small"
                              sx={{ 
                                background: `${item.column.color}30`,
                                border: `1px solid ${item.column.color}`,
                                color: item.column.color,
                              }}
                            />
                          )}
                        </Box>

                        {/* Сумма сделки */}
                        {item.dealAmount > 0 && (
                          <Chip
                            icon={<MoneyIcon sx={{ fontSize: 16 }} />}
                            label={formatCurrency(item.dealAmount)}
                            size="small"
                            sx={{ 
                              background: 'rgba(163, 190, 140, 0.15)',
                              border: '1px solid rgba(163, 190, 140, 0.3)',
                              color: '#a3be8c',
                              fontWeight: 700,
                            }}
                          />
                        )}

                        {/* Архивная метка */}
                        {item.archived && (
                          <Chip
                            icon={<ArchiveIcon sx={{ fontSize: 14 }} />}
                            label="В архиве"
                            size="small"
                            sx={{ 
                              background: 'rgba(139, 148, 158, 0.15)',
                              border: '1px solid rgba(139, 148, 158, 0.3)',
                              color: '#8b949e',
                            }}
                          />
                        )}

                        {/* Индикаторы */}
                        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                          {item.tasks?.length > 0 && (
                            <Tooltip title={`${item.tasks.filter(t => t.completed).length}/${item.tasks.length} задач`}>
                              <Chip
                                icon={<TaskIcon sx={{ fontSize: 14 }} />}
                                label={`${item.tasks.filter(t => t.completed).length}/${item.tasks.length}`}
                                size="small"
                                sx={{ height: 24, '& .MuiChip-label': { px: 1 } }}
                              />
                            </Tooltip>
                          )}
                          {item.stickers?.length > 0 && (
                            <Tooltip title={`${item.stickers.length} стикеров`}>
                              <Chip
                                icon={<StickerIcon sx={{ fontSize: 14 }} />}
                                label={item.stickers.length}
                                size="small"
                                sx={{ height: 24, '& .MuiChip-label': { px: 1 } }}
                              />
                            </Tooltip>
                          )}
                          {item.notes?.length > 0 && (
                            <Tooltip title={`${item.notes.length} заметок`}>
                              <Chip
                                icon={<NotesIcon sx={{ fontSize: 14 }} />}
                                label={item.notes.length}
                                size="small"
                                sx={{ height: 24, '& .MuiChip-label': { px: 1 } }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </AccordionSummary>
                    
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Divider sx={{ mb: 2 }} />
                      
                      <Grid container spacing={2}>
                        {/* Район */}
                        {item.district && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">Район</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              📍 {item.district}
                            </Typography>
                          </Grid>
                        )}
                        
                        {/* Описание */}
                        {item.description && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">Описание</Typography>
                            <Typography variant="body2">{item.description}</Typography>
                          </Grid>
                        )}

                        {/* Задачи */}
                        {item.tasks && item.tasks.length > 0 && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TaskIcon sx={{ fontSize: 18, color: '#a3be8c' }} />
                              Задачи
                            </Typography>
                            <Box sx={{ pl: 1 }}>
                              {item.tasks.map((task, idx) => (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Checkbox 
                                    checked={task.completed} 
                                    disabled 
                                    size="small"
                                    sx={{ p: 0 }}
                                  />
                                  <Typography 
                                    variant="body2"
                                    sx={{ 
                                      textDecoration: task.completed ? 'line-through' : 'none',
                                      color: task.completed ? 'text.secondary' : 'text.primary'
                                    }}
                                  >
                                    {task.text}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Grid>
                        )}

                        {/* Стикеры */}
                        {item.stickers && item.stickers.length > 0 && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <StickerIcon sx={{ fontSize: 18, color: '#ebcb8b' }} />
                              Стикеры
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {item.stickers.map((sticker, idx) => (
                                <Chip
                                  key={idx}
                                  label={sticker.text}
                                  size="small"
                                  sx={{
                                    background: `${sticker.color}30`,
                                    border: `1px solid ${sticker.color}`,
                                    color: sticker.color,
                                    fontWeight: 600,
                                  }}
                                />
                              ))}
                            </Box>
                          </Grid>
                        )}

                        {/* Заметки */}
                        {item.notes && item.notes.length > 0 && (
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <NotesIcon sx={{ fontSize: 18, color: '#d08770' }} />
                              Заметки
                            </Typography>
                            {item.notes.map((note, idx) => (
                              <Box 
                                key={idx}
                                sx={{
                                  p: 1.5,
                                  mb: 1,
                                  background: 'rgba(235, 203, 139, 0.1)',
                                  borderRadius: 1,
                                  borderLeft: '3px solid #ebcb8b',
                                }}
                              >
                                <Typography variant="body2">{note.text}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDateTime(note.createdAt)}
                                </Typography>
                              </Box>
                            ))}
                          </Grid>
                        )}

                        {/* Мета-информация */}
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary">
                              Создано: {formatDateTime(item.createdAt)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Обновлено: {formatDateTime(item.updatedAt)}
                            </Typography>
                            {item.createdBy && (
                              <Typography variant="caption" color="text.secondary">
                                Автор: {item.createdBy.firstName} {item.createdBy.lastName}
                              </Typography>
                            )}
                          </Box>
                        </Grid>

                        {/* Кнопка перехода в доску */}
                        {item.board?.name && (
                          <Grid item xs={12}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<OpenIcon />}
                              onClick={() => {
                                setHistoryOpen(false);
                                navigate(`/kanban/${encodeURIComponent(item.board.name)}`);
                              }}
                            >
                              Открыть доску "{item.board.name}"
                            </Button>
                          </Grid>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 4,
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                  borderRadius: 2,
                }}>
                  <Typography variant="body1" color="text.secondary">
                    📭 История работы с компанией пуста
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Добавьте карточку с этой компанией в любую доску
                  </Typography>
                </Box>
              )}
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default CompanyDetail;
